import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { search } from 'api/search-api';

import Meta from 'ui-kit/Meta';
import OverlayElement, { OverlayPane } from 'ui-kit/OverlayElement';

import ProjectName from 'components/project-name';

import IconProject from 'icons/icon-project';
import IconPlus from 'icons/icon-plus';

import { Task } from 'types';

import './AutocompleteDropdown.styles.css';

interface AutocompleteItemType {
    id: string,
    text: string,
    projectId?: string,
    project?: {
        id: string
    }
}

interface AutocompleteItemProps extends AutocompleteItemType {
    selected: boolean,
    index: number,
    onMouseEnter: (index: number) => void,
    onClick: (props: AutocompleteItemType) => void
}

export type Ctrl = {
    selectNext: () => void,
    selectPrev: () => void,
    getSelected: () => AutocompleteItemType | null
}

interface AutocompleteDropdownProps {
    resourceType: 'task' | 'milestone',
    projectId?: string,
    term: string,
    excludedResources?: string[],
    onSelect: (props: AutocompleteItemType) => void,
    refElement: HTMLElement | null,
    provideController?: {
        current: Ctrl | null
    }
}

const AutocompleteItem = ({ id, index, projectId, text, selected, onMouseEnter, onClick }: AutocompleteItemProps) => {
    const handleClick = () => {
        onClick({ id, projectId, text })
    }

    const handleMouseEnter = () => {
        onMouseEnter(index);
    }

    return (
        <div className={'autocomplete-item' + (selected ? ' selected' : '')} onMouseDown={handleClick} onMouseEnter={handleMouseEnter}>
            {
                id === 'create' ? (
                    <div className="autocomplete-item-icon">
                        <IconPlus />
                    </div>
                ) : null
            }

            <div className="autocomplete-item-main">
                <div className="autocomplete-item-body">
                    { text }
                </div>

                {
                    projectId ? (
                        <div className="autocomplete-item-foot">
                            <Meta items={[
                                {
                                    icon: <IconProject />,
                                    content: <ProjectName id={projectId} />
                                }
                            ]} />
                        </div>
                    ) : null
                }
            </div>
        </div>
    )
}

const AutocompleteDropdown = ({ resourceType, projectId, term, excludedResources, onSelect, refElement, provideController }: AutocompleteDropdownProps) => {
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const items = useMemo<AutocompleteItemType[]>(() => {
        const result = [];
        if (!isLoading && searchResults?.length) {
            searchResults.forEach((resource: any) => {
                if (resource) {
                    result.push({
                        id: resource.id,
                        projectId: resource.project?.id,
                        text: resource.name
                    });
                }
            });
        }

        result.push({
            id: 'create',
            text: `Create “${term}”`
        });

        return result;
    }, [isLoading, searchResults]);

    const selectPrev = useCallback(() => {
        setSelectedIndex(prevState => {
            let nextState = prevState - 1;

            if (nextState === -1) {
                nextState = items.length - 1;
            }

            return nextState;
        });
    }, [setSelectedIndex, items.length]);

    const selectNext = useCallback(() => {
        setSelectedIndex(prevState => {
            let nextState = prevState + 1;

            if (nextState >= items.length) {
                nextState = 0;
            }

            return nextState;
        });
    }, [setSelectedIndex, items.length]);

    const handleItemMouseEnter = (index: number) => {
        setSelectedIndex(index);
    }

    useEffect(() => {
        const ctrl = {
            selectPrev: () => {
                selectPrev();
            },
            selectNext: () => {
                selectNext();
            },
            getSelected: () => {
                return items[selectedIndex] || null;
            }
        };

        if (provideController) {
            provideController.current = ctrl;
        }

        return () => {
            if (provideController) {
                provideController.current = null;
            }
        }
    }, [items, selectedIndex, selectPrev, selectNext, provideController]);

    useEffect(() => {
        setSelectedIndex(0);

        search({ type: resourceType, term, projectId, limit: 10 })
            .then(response => {
                let results = response?.results || [];

                if (excludedResources?.length) {
                    results = results?.filter((item: any) => !excludedResources.includes(item.id));
                }

                setSearchResults(results);
            })
            .catch(e => {});
    }, [resourceType, term, projectId ]);

    return (
        <OverlayElement placement="bottom-start" refElement={refElement} inheritWidth={true} overflowPadding={0}>
            <OverlayPane className="autocomplete-dropdown">
                {
                    items.map((item, index) => (
                        <AutocompleteItem
                            key={item.id}
                            id={item.id}
                            index={index}
                            projectId={item.projectId || item.project?.id}
                            text={item.text}
                            selected={selectedIndex === index}
                            onMouseEnter={handleItemMouseEnter}
                            onClick={onSelect}
                        />
                    ))
                }
            </OverlayPane>
        </OverlayElement>
    )
}

export default AutocompleteDropdown;
